/**
 * RPS-RISKS-MATRIX
 * Situations RPS par fonction et par axe, puis liste des mesures avec leur statut.
 */
import Card from "@/components/card"
import Link from "@/components/link"
import ListItem from "@/components/list-item"
import SearchBar from "@/components/search-bar"
import CycleSortButton from "@/components/CycleSortButton"
import { useAppSelector } from "@/core/root-reducer"
import { useRisksSpace } from "@/core/risks-space.context"
import { faSortAlphaAsc, faSortAmountDown, faSortNumericDesc } from "@fortawesome/free-solid-svg-icons"
import { orderBy } from "lodash"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import populationStore from "../model/population.store"
import { Population } from "../model/population.types"
import { getRpsRisk, RPS_RISK_AXES, RpsRiskAxis } from "../model/rps-risk"
import RpsRiskEditModal from "./modal/RpsRiskEditModal"
import RpsEvaluationEditModal from "./modal/RpsEvaluationEditModal"
import preventionActionStore from "@/entities/prevention-action/model/prevention-action.store"
import { isRpsPreventionActionForPopulation } from "@/entities/prevention-action/model/prevention-action.types"
import { PREVENTION_MEASURES_VIEWS } from "@/entities/prevention-action/model/prevention-action.utils"
import PreventionActionAddModal from "@/entities/prevention-action/ui/modal/PreventionActionAddModal"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { PREVENTION_MEASURE_ICONS } from "@/entities/prevention-action/model/prevention-action.icons"
import getColor from "@/utils/get-color.utils"
import RpsRiskCell, { RPS_CELL_WIDTH, RPS_GLOBAL_CELL_WIDTH } from "./RpsRiskCell"

export default function RpsRisksMatrix() {
  const { isReadonly, getPath } = useRisksSpace()
  const navigate = useNavigate()
  const populations: Population[] = useAppSelector(populationStore.selectors.list)
  const rpsActions = useAppSelector((state) => preventionActionStore.selectors.listRps(state))
  const gridStyle = {
    gridTemplateColumns: `320px ${RPS_GLOBAL_CELL_WIDTH + 32}px repeat(${RPS_RISK_AXES.length}, ${RPS_CELL_WIDTH + 8}px) minmax(320px, 1fr)`
  }
  const [sort, setSort] = useState("risk")
  const [search, setSearch] = useState("")
  const [editing, setEditing] = useState<{ population: Population; axis: RpsRiskAxis } | null>(null)
  const [addingMeasureFor, setAddingMeasureFor] = useState<Population | null>(null)
  const [editingEvaluation, setEditingEvaluation] = useState<Population | null>(null)
  const sortItems = [
    { key: "risk", icon: faSortAmountDown, name: "Trier par niveau de risque" },
    { key: "size", icon: faSortNumericDesc, name: "Trier par effectif" },
    { key: "name", icon: faSortAlphaAsc, name: "Trier par nom" }
  ]
  const filtered = orderBy(
    populations.filter((population) => population.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())),
    [
      (population) =>
        sort === "risk"
          ? (population.cRpsLevel ?? -1)
          : sort === "size"
            ? population.size
            : population.name.toLocaleLowerCase(),
      "name"
    ],
    [sort === "name" ? "asc" : "desc", "asc"]
  )

  return (
    <>
      {editing && (
        <RpsRiskEditModal population={editing.population} axis={editing.axis} onClose={() => setEditing(null)} />
      )}
      {addingMeasureFor && (
        <PreventionActionAddModal rpsPopulationIds={[addingMeasureFor.id]} onClose={() => setAddingMeasureFor(null)} />
      )}
      {editingEvaluation && (
        <RpsEvaluationEditModal population={editingEvaluation} onClose={() => setEditingEvaluation(null)} />
      )}
      <Card isWithoutPadding className="w-full min-w-0 overflow-hidden">
        <div className="overflow-auto max-h-[calc(100vh-240px)]">
          <div
            role="table"
            aria-label="Situations RPS par fonction et par axe"
            className="text-base font-normal"
            style={{
              width: "100%",
              minWidth: 320 + RPS_GLOBAL_CELL_WIDTH + 32 + RPS_RISK_AXES.length * (RPS_CELL_WIDTH + 8) + 320
            }}>
            <div role="row" className="sticky top-0 z-20 grid bg-white" style={gridStyle}>
              <div
                role="columnheader"
                aria-label="Fonction"
                className="sticky left-0 z-30 flex h-[150px] flex-col box-border bg-white px-[20px] pt-[20px] pb-[14px]">
                <div className="flex items-center gap-[8px] font-normal">
                  <div className="flex-1 min-w-0">
                    <SearchBar searchText={search} onSearch={setSearch} placeholder="Rechercher…" width="100%" />
                  </div>
                  <CycleSortButton items={sortItems} activeKey={sort} onChange={(item) => setSort(item.key)} />
                </div>
                <div className="mt-auto font-bold">Fonction</div>
              </div>
              <div
                role="columnheader"
                className="flex h-[150px] box-border items-end justify-center pl-[4px] pr-[28px] pb-[14px] text-base font-bold">
                Risque
              </div>
              {RPS_RISK_AXES.map((axis) => (
                <div role="columnheader" key={axis.id} className="relative h-[150px]">
                  <div className="absolute bottom-[14px] left-[38px] w-[130px] origin-bottom-left -rotate-45 text-sm font-normal not-italic [text-wrap:balance]">
                    {axis.id === "value-conflicts" ? (
                      <>
                        Conflits de
                        <br />
                        valeurs
                      </>
                    ) : (
                      axis.label.replace(/\b(au|du|le) travail\b/g, "$1\u00a0travail")
                    )}
                  </div>
                </div>
              ))}
              <div role="columnheader" aria-label="Mesures" />
            </div>
            {filtered.map((population) => {
              const measures = rpsActions.filter((action) => isRpsPreventionActionForPopulation(action, population.id))
              return (
                <ListItem
                  key={population.id}
                  isSmall
                  isEditable
                  onClick={() => setEditingEvaluation(population)}
                  className="!p-0 !flex-none w-full group/row">
                  <div role="row" className="grid w-full items-stretch" style={gridStyle}>
                    <div
                      role="rowheader"
                      className="sticky left-0 z-10 bg-white group-hover/row:bg-slate-50 flex min-w-0 items-center px-[20px] py-[8px] font-normal">
                      <span className="min-w-0 truncate" onClick={(event) => event.stopPropagation()}>
                        <Link
                          isWithoutMargin
                          className="!inline"
                          onClick={() => navigate(getPath(`/populations/${population.id}`))}>
                          {population.name}
                        </Link>
                      </span>
                    </div>
                    <div role="cell" className="flex items-center pl-[4px] pr-[28px] py-[8px]">
                      <RpsRiskCell
                        level={population.cRpsLevel}
                        isGlobal
                        label="Niveau de risque"
                        onClick={() => setEditingEvaluation(population)}
                      />
                    </div>
                    {RPS_RISK_AXES.map((axis) => {
                      const risk = getRpsRisk(population.rpsRisks, axis.id)
                      return (
                        <div role="cell" key={axis.id} className="flex items-center px-[4px] py-[8px]">
                          <RpsRiskCell
                            axis={axis.id}
                            level={risk?.level ?? null}
                            label={`${axis.label}, ${population.name}`}
                            onClick={isReadonly ? undefined : () => setEditing({ population, axis: axis.id })}
                          />
                        </div>
                      )
                    })}
                    <div
                      role="cell"
                      className="flex min-w-0 items-center pl-[24px] pr-[20px] py-[6px]"
                      onClick={(event) => event.stopPropagation()}>
                      <ul className="m-0 flex w-full min-w-0 list-none flex-col gap-[4px] p-0 empty:hidden">
                        {(["in-place", "planned"] as const).map((status) => {
                          const isDone = status === "in-place"
                          const statusMeasures = measures.filter((measure) => (measure.cStatus === "done") === isDone)
                          if (!statusMeasures.length) return null
                          const label = PREVENTION_MEASURES_VIEWS[status].title
                          const text =
                            statusMeasures.length > 1
                              ? `${statusMeasures.length} ${label.toLocaleLowerCase()}`
                              : statusMeasures[0].name
                          return (
                            <li key={status} className="min-w-0 overflow-hidden">
                              <button
                                type="button"
                                disabled={isReadonly}
                                className="flex w-full min-w-0 items-center gap-[6px] p-0 text-left text-sm font-normal"
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: getColor("blue"),
                                  cursor: isReadonly ? "default" : "pointer",
                                  fontFamily: "inherit"
                                }}
                                onClick={() => navigate(getPath(`/populations/${population.id}`))}>
                                <FontAwesomeIcon icon={PREVENTION_MEASURE_ICONS[status]} className="shrink-0" />
                                <span
                                  className="min-w-0 truncate"
                                  style={{ color: isDone ? "#8C8C8C" : "inherit" }}>
                                  {text}
                                </span>
                              </button>
                            </li>
                          )
                        })}
                      </ul>
                      {!measures.length &&
                        (isReadonly ? (
                          <span className="grey-t text-sm">—</span>
                        ) : (
                          <Link
                            isWithoutMargin
                            className="invisible group-hover/row:visible"
                            onClick={() => setAddingMeasureFor(population)}>
                            Ajouter une mesure
                          </Link>
                        ))}
                    </div>
                  </div>
                </ListItem>
              )
            })}
            {!filtered.length && (
              <p className="p-[24px] grey-t text-center">
                {populations.length ? "Aucune fonction trouvée." : "Aucune fonction dans l’établissement."}
              </p>
            )}
          </div>
        </div>
      </Card>
    </>
  )
}
